import { BroadcastTutorialStep } from '../../../../../common/types/broadcast';
import { UserSettings } from '../../../../../common/types/settings';
import { isDOMAvailable } from '../../helpers/dom';
import Storage from '../../helpers/storage';

const CHECK_DESKTOP_APP_EXISTENCE_COOKIE = 'checkDesktopAppExistence';
const IS_SHOWN_LIMIT_MESSAGE_COOKIE = 'isShownLimitMessage';
const GRID_VIEW_STORAGE_KEY = 'gridView';
const SHOW_MY_VIDEO_STORAGE_KEY = 'showMyVideo';
const JOIN_WITH_CAMERA_OFF_STORAGE_KEY = 'joinWithCameraOff';
const JOIN_WITH_MICROPHONE_OFF_STORAGE_KEY = 'joinWithMicrophoneOff';
const SUMMARIZATION_WELCOME_POPUP_STORAGE_KEY = 'summarizationWelcomePopupWasShown';
const SUMMARIZATION_START_POPUP_STORAGE_KEY = 'summarizationStartPopupWasShown';
const BROADCAST_TUTORIAL_STEP_STORAGE_KEY = 'broadcastTutorialStep';
const BROADCAST_CAPTION_TOOLTIP_SHOWN_STORAGE_KEY = 'broadcastCaptionTooltipShown';

export const LOCAL_ONPREM_THEME_STORAGE_KEY = 'telemostOnPremGeneralThemes';

type PersistenceContext = {
    isOnPremise: boolean;
    userUid?: string;
};

export const canUseDatasyncPersistence = ({ isOnPremise, userUid }: PersistenceContext) =>
    Boolean(userUid) && !isOnPremise;

const getCookieValue = (name: string): string | undefined => {
    if (!isDOMAvailable()) {
        return;
    }

    const cookieString = document.cookie;

    if (!cookieString) {
        return;
    }

    const part = cookieString.split(/;\s*/).find((cookie) => cookie.startsWith(`${name}=`));

    return part?.slice(name.length + 1);
};

const setCookieValue = (name: string, value: string): void => {
    if (!isDOMAvailable()) {
        return;
    }

    document.cookie = `${name}=${value}`;
};

const isBroadcastTutorialStep = (value: string | null): value is BroadcastTutorialStep => {
    return value !== null && Object.values(BroadcastTutorialStep).includes(value as BroadcastTutorialStep);
};

export async function loadLocalUserSettings({
    isOnPremise,
}: Pick<PersistenceContext, 'isOnPremise'>): Promise<Partial<UserSettings>> {
    const localSettings: Partial<UserSettings> = {
        showMyVideo: await Storage.getParsedItem(SHOW_MY_VIDEO_STORAGE_KEY),
        joinWithCameraOff: await Storage.getParsedItem(JOIN_WITH_CAMERA_OFF_STORAGE_KEY),
        joinWithMicrophoneOff: await Storage.getParsedItem(JOIN_WITH_MICROPHONE_OFF_STORAGE_KEY),
        summarizationWelcomePopupWasShown: await Storage.getParsedItem(SUMMARIZATION_WELCOME_POPUP_STORAGE_KEY),
        summarizationStartPopupWasShown: await Storage.getParsedItem(SUMMARIZATION_START_POPUP_STORAGE_KEY),
        gridView2: (await Storage.getItem(GRID_VIEW_STORAGE_KEY)) ?? undefined,
        checkDesktopAppExistence: getCookieValue(CHECK_DESKTOP_APP_EXISTENCE_COOKIE) !== '0',
        isShownLimitMessage: getCookieValue(IS_SHOWN_LIMIT_MESSAGE_COOKIE) === '1',
    };

    if (!isOnPremise) {
        return localSettings;
    }

    const broadcastTutorialStep = await Storage.getItem(BROADCAST_TUTORIAL_STEP_STORAGE_KEY);

    if (isBroadcastTutorialStep(broadcastTutorialStep)) {
        localSettings.broadcastTutorialStep = broadcastTutorialStep;
    }

    const broadcastCaptionTooltipShownRaw = await Storage.getItem(BROADCAST_CAPTION_TOOLTIP_SHOWN_STORAGE_KEY);
    const broadcastCaptionTooltipShown =
        broadcastCaptionTooltipShownRaw !== null ? JSON.parse(broadcastCaptionTooltipShownRaw) : undefined;

    if (typeof broadcastCaptionTooltipShown === 'boolean') {
        localSettings.broadcastCaptionTooltipShown = broadcastCaptionTooltipShown;
    }

    return localSettings;
}

export async function persistLocalUserSetting(name: keyof UserSettings, value: string | boolean): Promise<void> {
    switch (name) {
        case 'showMyVideo':
            return Storage.setItem(SHOW_MY_VIDEO_STORAGE_KEY, JSON.stringify(value));
        case 'joinWithCameraOff':
            return Storage.setItem(JOIN_WITH_CAMERA_OFF_STORAGE_KEY, JSON.stringify(value));
        case 'joinWithMicrophoneOff':
            return Storage.setItem(JOIN_WITH_MICROPHONE_OFF_STORAGE_KEY, JSON.stringify(value));
        case 'summarizationWelcomePopupWasShown':
            return Storage.setItem(SUMMARIZATION_WELCOME_POPUP_STORAGE_KEY, JSON.stringify(value));
        case 'summarizationStartPopupWasShown':
            return Storage.setItem(SUMMARIZATION_START_POPUP_STORAGE_KEY, JSON.stringify(value));
        case 'gridView2':
            return Storage.setItem(GRID_VIEW_STORAGE_KEY, String(value));
        case 'checkDesktopAppExistence':
            setCookieValue(CHECK_DESKTOP_APP_EXISTENCE_COOKIE, value ? '1' : '0');
            return Promise.resolve();
        case 'isShownLimitMessage':
            setCookieValue(IS_SHOWN_LIMIT_MESSAGE_COOKIE, value ? '1' : '0');
            return Promise.resolve();
        case 'broadcastTutorialStep':
            return Storage.setItem(BROADCAST_TUTORIAL_STEP_STORAGE_KEY, String(value));
        case 'broadcastCaptionTooltipShown':
            return Storage.setItem(BROADCAST_CAPTION_TOOLTIP_SHOWN_STORAGE_KEY, JSON.stringify(value));
        default:
            return Promise.resolve();
    }
}
